import React from "react";
import Resources from "../../types/Resources";
import usePermitsTable from "../../hooks/usePermitsTable";
import LoadingSpinner from "../Common/LoadingSpinner";
import PermitTableRow from "./PermitTableRow";
import Button from "../Common/Button";
import {
    CodeStatus,
    HarbourOffice,
    PermitCategory,
    PermitTypeFilter,
    WorkPermitSortProperty
} from "../../common/enums";
import PositionDisplayModal from "../PositionDisplayModal";
import Department from "../../types/Department";
import WorkPermitFilterControls from "../WorkPermitFilterControls";
import PaginationControls from "../Common/PaginationControls";
import WorkPermitFilter from "../../types/filters/WorkPermitFilter";

export type PermitsTableProps = {
    permitSeekerId: string | undefined;
    defaultFilter: WorkPermitFilter;
    resources: Resources;
    allDepartmentFilterOptions: Department[];
    allStatusFilterOptions: CodeStatus[];
    allCategoryFilterOptions: PermitCategory[];
    allEnergyHarbourPermitTypeOptions: PermitTypeFilter[];
    allPortControlPermitTypeOptions: PermitTypeFilter[];
    allHarbourOfficeFilterOptions: HarbourOffice[];
};

function PermitsTable(props: PermitsTableProps) {
    const {
        workPermitFilter,
        paginatedContent,
        isLoading,
        showMapModal,
        mapModalPermit,
        permitSeeker,
        deleteButtonClickHandlerAsync,
        checkoutButtonClickHandlerAsync,
        copyButtonClickHandlerAsync,
        mapButtonClickHandler,
        renewButtonClickHandlerAsync,
        columnHeaderClickHandlerAsync,
        hideMapModalHandler,
        workPermitFilterChangeHandler,
        applyFilterHandlerAsync,
        pageIndexChangeHandlerAsync,
        pageSizeChangeHandlerAsync
    } = usePermitsTable(props);

    const { resources } = props;

    if (isLoading) {
        return <LoadingSpinner />;
    }

    const rows = !!paginatedContent
        ? paginatedContent.permits.map((p) => (
              <PermitTableRow
                  key={p.id}
                  permit={p}
                  resources={resources}
                  onDeleteButtonClick={() => deleteButtonClickHandlerAsync(p)}
                  onCheckoutButtonClick={() => checkoutButtonClickHandlerAsync(p)}
                  onCopyButtonClick={() => copyButtonClickHandlerAsync(p)}
                  onMapButtonClick={() => mapButtonClickHandler(p)}
                  onRenewPermitButtonClick={() => renewButtonClickHandlerAsync(p)}
              />
          ))
        : [];

    return (
        <>
            <div className="container-fluid text-center mb-2 p-0 mt-3">
                <div className="d-flex align-items-center justify-content-between ps-3 pe-3">
                    <div>
                        {permitSeeker ? (
                            <div className="d-flex flex-column align-items-start">
                                <h1 className="h5 m-0">{resources.get("PermitSeeker")}</h1>
                                <p className="m-0" style={{ fontSize: "large" }}>
                                    {permitSeeker.fullName} ({permitSeeker.emailAddress})
                                </p>
                                <p className="m-0" style={{ fontSize: "large" }}>
                                    {permitSeeker.companyName}
                                </p>
                            </div>
                        ) : null}
                    </div>
                    <WorkPermitFilterControls
                        resources={props.resources}
                        allPermitCategoryOptions={props.allCategoryFilterOptions}
                        allDepartmentOptions={props.allDepartmentFilterOptions}
                        allHarbourOfficeOptions={props.allHarbourOfficeFilterOptions}
                        allEnergyHarbourPermitTypeOptions={props.allEnergyHarbourPermitTypeOptions}
                        allPortControlPermitTypeOptions={props.allPortControlPermitTypeOptions}
                        allStatusOptions={props.allStatusFilterOptions}
                        filter={workPermitFilter}
                        onApplyFilter={applyFilterHandlerAsync}
                        onFilterChange={workPermitFilterChangeHandler}
                    />
                </div>
                <table className="table table-striped permits-table mt-2 p-0">
                    <thead>
                        <tr>
                            <th scope="col" className="thin-col" style={{ width: "2%" }}>
                                <Button
                                    variant="link"
                                    onClick={() => columnHeaderClickHandlerAsync(WorkPermitSortProperty.Status)}
                                    className="text-nowrap position-relative"
                                >
                                    {sortIcon(WorkPermitSortProperty.Status, workPermitFilter)}
                                    <span>{resources.get("StatusCode")}</span>
                                </Button>
                            </th>
                            <th scope="col" style={{ width: "5%" }}>
                                <Button
                                    variant="link"
                                    onClick={() =>
                                        columnHeaderClickHandlerAsync(WorkPermitSortProperty.SequenceIdentifier)
                                    }
                                    className="text-nowrap"
                                >
                                    {sortIcon(WorkPermitSortProperty.SequenceIdentifier, workPermitFilter)}
                                    <span>{resources.get("SequenceIdentifier")}</span>
                                </Button>
                            </th>
                            <th scope="col" style={{ width: "5%" }}>
                                <Button
                                    variant="link"
                                    onClick={() => columnHeaderClickHandlerAsync(WorkPermitSortProperty.StartDate)}
                                    className="text-nowrap"
                                >
                                    {sortIcon(WorkPermitSortProperty.StartDate, workPermitFilter)}
                                    <span>{resources.get("StartDate")}</span>
                                </Button>
                            </th>
                            <th scope="col" style={{ width: "5%" }}>
                                <Button
                                    variant="link"
                                    onClick={() => columnHeaderClickHandlerAsync(WorkPermitSortProperty.EndDate)}
                                    className="text-nowrap"
                                >
                                    {sortIcon(WorkPermitSortProperty.EndDate, workPermitFilter)}
                                    <span>{resources.get("EndDate")}</span>
                                </Button>
                            </th>
                            <th scope="col">
                                <Button
                                    variant="link"
                                    onClick={() => columnHeaderClickHandlerAsync(WorkPermitSortProperty.Positions)}
                                    className="text-nowrap"
                                >
                                    {sortIcon(WorkPermitSortProperty.Positions, workPermitFilter)}
                                    <span>{resources.get("Location")}</span>
                                </Button>
                            </th>
                            <th scope="col" style={{ width: "6%" }}>
                                <Button
                                    variant="link"
                                    onClick={() =>
                                        columnHeaderClickHandlerAsync(WorkPermitSortProperty.CompanyNormalizedName)
                                    }
                                    className="text-nowrap"
                                >
                                    {sortIcon(WorkPermitSortProperty.CompanyNormalizedName, workPermitFilter)}
                                    <span>{resources.get("Company")}</span>
                                </Button>
                            </th>
                            <th scope="col" style={{ width: "5%" }}>
                                <Button
                                    variant="link"
                                    onClick={() => columnHeaderClickHandlerAsync(WorkPermitSortProperty.DepartmentName)}
                                    className="text-nowrap"
                                >
                                    {sortIcon(WorkPermitSortProperty.DepartmentName, workPermitFilter)}
                                    <span>{resources.get("Department")}</span>
                                </Button>
                            </th>
                            <th scope="col" style={{ width: "5%" }}>
                                <Button
                                    variant="link"
                                    onClick={() => columnHeaderClickHandlerAsync(WorkPermitSortProperty.Entrepreneur)}
                                    className="text-nowrap"
                                >
                                    {sortIcon(WorkPermitSortProperty.Entrepreneur, workPermitFilter)}
                                    <span>{resources.get("PerformerOrEntreprenour")}</span>
                                </Button>
                            </th>
                            <th scope="col">
                                <Button
                                    variant="link"
                                    onClick={() => columnHeaderClickHandlerAsync(WorkPermitSortProperty.Description)}
                                    className="text-nowrap"
                                >
                                    {sortIcon(WorkPermitSortProperty.Description, workPermitFilter)}
                                    <span>{resources.get("Description")}</span>
                                </Button>
                            </th>
                        </tr>
                    </thead>
                    <tbody>{rows}</tbody>
                </table>
                {paginatedContent ? (
                    <PaginationControls
                        currentPage={workPermitFilter.pageIndex}
                        currentPageSize={workPermitFilter.pageSize}
                        onPageChange={pageIndexChangeHandlerAsync}
                        onPageSizeChange={pageSizeChangeHandlerAsync}
                        totalPages={paginatedContent.numberOfPages}
                        resources={resources}
                    />
                ) : null}
            </div>
            <PositionDisplayModal
                onHide={hideMapModalHandler}
                show={showMapModal}
                permit={mapModalPermit}
                resources={resources}
            />
        </>
    );
}

export default PermitsTable;

function sortIcon(sortProperty: WorkPermitSortProperty, filter: WorkPermitFilter): React.ReactNode {
    if (filter.sortProperty !== sortProperty) {
        return null;
    }

    const sortClass = filter.isDescending ? "fas fa-sort-down" : "fas fa-sort-up";

    return (
        <div className="position-absolute" style={{ marginLeft: -12, marginTop: filter.isDescending ? -3 : 3 }}>
            <i className={sortClass}></i>
        </div>
    );
}
